import { getConfig } from "@edx/frontend-platform";
import { getAuthenticatedUser } from "@edx/frontend-platform/auth";
import dayjs from "dayjs";

import useIsSpeechcraftMFE from "../../hooks/useIsSpeechcraftMFE";
import { getUserName } from "../../utils/utility";
import {
  NOTIFICATION_TYPES,
  APPROVAL_REQUEST_STATUS,
  BADGE_TYPES,
  BADGE_HASH_TYPES,
  TICourses,
  EXPLORATIONS_CONTENT_GROUP_VALUE
} from "../../config";

const getLevelPathCompletionText = (
  requestType,
  status,
  isLastLevel,
  levelNo,
  pathName,
  approvedBy,
  levelName,
  isExploration,
) => {
  const isLevelRequest = requestType?.toLocaleLowerCase() === "level";
  const isPathRequest = requestType?.toLocaleLowerCase() === "path";

  const isLevelOrPathApproved = status === APPROVAL_REQUEST_STATUS.Approved;
  const isLevelOrPathDenied = status === APPROVAL_REQUEST_STATUS.Denied;
  const isLevelOrPathReversed = status === APPROVAL_REQUEST_STATUS.Reversed;

  const isDTMPath = pathName.toLowerCase().startsWith(TICourses.DTM);
  const isPMPPath = pathName.toLowerCase().startsWith(TICourses.PMP);

  if (isLevelOrPathApproved || isLevelOrPathReversed) {
    if (isDTMPath) {
      return "🎉You finished the Distinguished Toastmaster project; congratulations on your accomplishment! Where will your learning journey take you next?";
    }

    if (isPMPPath) {
      return "✅ You've completed the Pathways Mentor Program! Reach out to your VPE about becoming a mentor.";
    }

    if (isExploration && isLevelRequest) {
      return `🎉 Your ${levelName} completion for ${pathName} is approved. Review the Exploration Culmination and make a plan to complete this exploration.`;
    }

    if (isExploration && isPathRequest) {
      return `🎉 You finished the ${pathName} exploration; congratulations on your accomplishment! Where will your learning journey take you next?`;
    }

    if (isLastLevel && isLevelRequest) {
      return `🎉 Your Level ${levelNo} completion is approved! Review the final project and make a plan to complete this path.`;
    }

    if (isLevelRequest) {
      return `🎉 Your Level ${levelNo} completion is approved! Explore Level ${++levelNo} now.`;
    }

    if (isPathRequest) {
      return `🎉 You finished the ${pathName} path; congratulations on your accomplishment! Where will your learning journey take you next?`;
    }
  }

  if (isLevelOrPathDenied) {
    if (isDTMPath) {
      return `❗ Action needed: Your completion request for the DTM project was denied by ${getUserName(
        approvedBy
      )}.`;
    }

    if (isPMPPath) {
      return `❗ Action needed: Your completion request for the Pathways Mentor Program was denied by ${getUserName(
        approvedBy
      )}.`;
    }

    if (isExploration && isLevelRequest) {
      return `❗ Action needed: Your completion request for ${levelName} of ${pathName} was denied by ${getUserName(
        approvedBy,
      )}. Learn what to fix and resubmit.`;
    }

    if (isExploration && isPathRequest) {
      return `❗ Action needed: Your completion request for ${pathName} exploration was denied by ${getUserName(
        approvedBy,
      )}. Learn what to fix and resubmit.`;
    }

    if (isLevelRequest) {
      return `❗ Action needed: Your completion request for Level ${levelNo} of the ${pathName} path was denied by ${getUserName(
        approvedBy
      )}. Learn what to fix and resubmit.`;
    }

    return `❗ Action needed: Your completion request for ${pathName} path was denied by ${getUserName(
      approvedBy
    )}. Learn what to fix and resubmit.`;
  }

  return "";
};

const getNotificationSenderName = (
  sender,
  notificationType,
  requestType,
  isApprovedByAdmin,
  levelName,
  isExploration,
) => {
  if (notificationType === NOTIFICATION_TYPES.completionReviewNotifyBCM) {
    const isLevelRequest = requestType?.toLocaleLowerCase() === 'level';

    if (isLevelRequest && isExploration) {
      return `${levelName} Completion`
    } else if(isExploration) {
      return 'Exploration Completion'
    }

    return `${requestType} Completion`;
  }

  const senderName = getUserName(sender)?.trim();
  if (
    (notificationType === NOTIFICATION_TYPES.completionReview &&
      isApprovedByAdmin) ||
    !senderName
  ) {
    return "System Administrator";
  }

  return senderName;
};

const getFeedbackNotificationText = (notification = {}) => {
  const { target = {} } = notification;
  const { isSenderCoordinator = false, isReceiverCoordinator = false } = target;
  const isSenderSpeechcrafter = !isSenderCoordinator;
  const isReceiverSpeechcrafter = !isReceiverCoordinator;
  if (notification.isSpeechcraft) {
    if (isSenderSpeechcrafter && isReceiverSpeechcrafter) {
      return `✏️ ${getUserName(notification.sender, true)} gave you feedback!`;
    }

    if (isSenderSpeechcrafter && isReceiverCoordinator) {
      return `📧 ${getUserName(
        notification.sender
      )} sent you a message! View and respond.`;
    }

    return `✏️ Your Speechcraft Coordinator, ${getUserName(
      notification.sender
    )}, gave you feedback!`;
  }

  return `✏️ ${getUserName(notification.sender, true)} gave you feedback!`;
};

const noTargetExceptions = [
  NOTIFICATION_TYPES.reverse_completion_project,
  NOTIFICATION_TYPES.reverse_completion_level,
  NOTIFICATION_TYPES.reverse_completion_path,
];

const getCompletionRequestText = (
  member, 
  requestType, 
  levelNo, 
  pathName,
  levelName,
  isExploration,
) => {
  const pathNameLower = pathName.toLowerCase();

  if (pathNameLower.startsWith(TICourses.DTM)) {
    return `❗Action needed: Review and approve/deny ${getUserName(
      member,
      true
    )}'s DTM project completion request in the approvals section of the Base Camp Manager page.`;
  }

  if (pathNameLower.startsWith(TICourses.PMP)) {
    return `❗Action needed: Review and approve/deny ${getUserName(
      member,
      true
    )}'s Pathways Mentor Program completion request in the approvals section of the Base Camp Manager page.`;
  }

  const isLevelRequest = requestType?.toLocaleLowerCase() === 'level';

  const completionLabel = isLevelRequest
    ? (isExploration
        ? `${levelName} for ${pathName}`
        : `Level ${levelNo}`)
    : (isExploration
        ? `${pathName} exploration`
        : `${pathName} path`);

  return `❗Action needed: Review and approve/deny ${getUserName(
    member,
    true
  )}'s ${completionLabel} completion request on the "Approvals" page.`;
};

const getCompletionReviewNotifyBCMText = (
  isApprovedByAdmin,
  approvedBy,
  status,
  requestType,
  member,
  levelNo,
  pathName,
  modified,
  levelName,
  isExploration,
) => {
  const pathNameLower = pathName.toLowerCase();

  if (pathNameLower.startsWith(TICourses.DTM)) {
    return `✅ Action no longer needed: ${
      isApprovedByAdmin ? "System Administrator" : getUserName(approvedBy)
    } ${status?.toLocaleLowerCase()} ${getUserName(
      member
    )}'s DTM project completion request on ${dayjs(modified)?.format(
      "MMMM DD"
    )}.`;
  }

  if (pathNameLower.startsWith(TICourses.PMP)) {
    return `✅ Action no longer needed: ${
      isApprovedByAdmin ? "System Administrator" : getUserName(approvedBy)
    } ${status?.toLocaleLowerCase()} ${getUserName(
      member
    )}'s Pathways Mentor Program completion request on ${dayjs(
      modified
    )?.format("MMMM DD")}.`;
  }

  const isLevelRequest = requestType?.toLocaleLowerCase() === 'level';

  const completionLabel = isLevelRequest
    ? (isExploration
        ? levelName
        : `Level ${levelNo} ${pathName}`)
    : (isExploration
        ? `${pathName} exploration`
        : `${pathName} path`);

  return `✅ Action no longer needed: ${
    isApprovedByAdmin ? 'System Administrator' : getUserName(approvedBy)
  } ${status?.toLocaleLowerCase()} ${getUserName(member)}'s ${completionLabel} completion request on ${dayjs(
    modified,
  )?.format('MMMM DD')}.`;
};

const getCompletionRequestReminderText = (
  member,
  requestType,
  levelNo,
  pathName,
  type,
  levelName,
  isExploration,
) => {
  const pathNameLower = pathName.toLowerCase();

  if (pathNameLower.startsWith(TICourses.DTM)) {
    return `❗ Action needed: Approve ${getUserName(
      member,
      true
    )}'s DTM project completion request. This request was submitted ${
      type === NOTIFICATION_TYPES.completionRequestSecondReminder
        ? "3 weeks"
        : "7 days"
    } ago.`;
  }

  if (pathNameLower.startsWith(TICourses.PMP)) {
    return `❗ Action needed: Approve ${getUserName(
      member,
      true
    )}'s Pathways Mentor Program completion request. This request was submitted ${
      type === NOTIFICATION_TYPES.completionRequestSecondReminder
        ? "3 weeks"
        : "7 days"
    } ago.`;
  }

  const isLevelRequest = requestType?.toLocaleLowerCase() === 'level';

  const completionLabel = isLevelRequest
    ? (isExploration ? levelName : `Level ${levelNo}`)
    : (isExploration ? `${pathName} exploration` : `${pathName} path`);

  return `❗ Action needed: Approve ${getUserName(member, true)}'s ${completionLabel} completion request. Request was submitted ${
    type === NOTIFICATION_TYPES.completionRequestSecondReminder
      ? '3 weeks'
      : '7 days'
  } ago.`;
};

// eslint-disable-next-line
export const useNotifications = (
  notification = {},
  shouldOpenModal = false
) => {
  const { type, target, slug, data, isSpeechcraft } = notification;
  const isSpeechcraftMFE = useIsSpeechcraftMFE();
  const isSpeechcraftLearning = isSpeechcraftMFE
    ? "speechcraftlearning"
    : "learning";
  const BASE_URL = isSpeechcraftMFE
    ? getConfig().SPEECHCRAFT_MFE_BASE_URL
    : getConfig().DASHBOARD_MFE_BASE_URL;

  const user = getAuthenticatedUser();

  const {
    type: requestType,
    courseName,
    status,
    courseId,
    isSubmitted: isEvalFormSumitted,
    approvedBy,
    member,
    speechLog,
    isLastLevel,
    levelNo,
    levelName,
    pathName,
    contentGroup,
    modified,
    badge,
    enrollment,
    isApprovedByAdmin,
    asset,
    fileName,
    isShifted,
  } = target || {};
  const { badgeType, badgeMeta } = badge || {};
  const isExploration = 
    (contentGroup || '').toLowerCase() === EXPLORATIONS_CONTENT_GROUP_VALUE;

  const getNotificationText = () => {
    if (!target && !noTargetExceptions.includes(type)) {
      return {
        notificationText:
          "This notification is no longer available. This can happen if the action was reversed or the member no longer belongs to your club.",
        redirectRoute: ``,
        isDeleted: true,
      };
    }

    if (isShifted) {
      return {
        notificationText:
          "This notification is no longer available. This can happen if the credit association was changed.",
        redirectRoute: ``,
        isDeleted: true,
      };
    }

    const checkCondition =
      (target && target?.status?.toLowerCase() === "pending") ||
      (target &&
        target?.status?.toLowerCase() !== "pending" &&
        approvedBy?.id === user?.userId);

    switch (type) {
      case NOTIFICATION_TYPES.feedbackSent:
        return {
          notificationText: getFeedbackNotificationText(notification),
          redirectRoute: `${BASE_URL}/feedback/${
            isSpeechcraft ? "messages-feedback" : "member-feedback"
          }`,
        };

      case NOTIFICATION_TYPES.feedbackRequested:
        return {
          notificationText: notification.isSpeechcraft
            ? `🔔 ${getUserName(
                notification.sender
              )} requested feedback from you! Give them your thoughts.`
            : `${getUserName(
                notification.sender,
                true
              )} sent you a feedback request. View their request and provide specific comments.`,
          redirectRoute: shouldOpenModal
            ? `${BASE_URL}/notifications/${slug}`
            : `${BASE_URL}/notifications/`, //redirect to notification
        };

      case NOTIFICATION_TYPES.courseEnrollment: {
        const label = isExploration ? 'Exploration' : 'Path';
        return {
          notificationText: `You have successfully enrolled in ${courseName} ${label}`,
          redirectRoute: `/${isSpeechcraftLearning}/course/${courseId}/home`,
        };
      }        

      case NOTIFICATION_TYPES.clubAssociationChange: {
        const isLevelRequest = requestType?.toLocaleLowerCase() === 'level';
        const explorationLevelLabel = `${levelName} of ${pathName}`

        const completionLabel = isLevelRequest
          ? (isExploration ? explorationLevelLabel : `Level ${levelNo} of ${pathName}`)
          : (isExploration ? `${pathName} exploration` : `${pathName} path`);
          
        return {
          notificationText: `${getUserName(
            member,
            true
          )} has transferred credit for their ${completionLabel} completion to your club. No action is needed from you as this request was approved by another club.`,
          redirectRoute: ``,
          isDeleted: true,
        };
      }

      case NOTIFICATION_TYPES.completionRequest:
        return {
          notificationText: getCompletionRequestText(
            member,
            requestType,
            levelNo,
            pathName,
            levelName,
            isExploration
          ),
          redirectRoute: checkCondition
            ? `${BASE_URL}/bcm-dashboard/approvals/${slug}`
            : "",
          isDeleted: !checkCondition,
        };

      case NOTIFICATION_TYPES.completionReview:
        return {
          notificationText: getLevelPathCompletionText(
            requestType,
            status,
            isLastLevel,
            levelNo,
            pathName,
            approvedBy,
            levelName,
            isExploration
          ),
          redirectRoute:
            status === "Denied"
              ? shouldOpenModal
                ? `${BASE_URL}/notifications/${slug}`
                : `${BASE_URL}/notifications/`
              : `/${isSpeechcraftLearning}/course/${courseId}/home`,
        };

      case NOTIFICATION_TYPES.feedbackBadgeEarned:
        return {
          notificationText:
            notification.isSpeechcraft &&
            notification.target?.isSenderCoordinator
              ? `🏆 Your Speechcraft Coordinator, ${getUserName(
                  notification.sender,
                  true
                )}, gave you the ${badgeMeta?.name} badge!`
              : `🏆 ${getUserName(notification.sender, true)} gave you the ${
                  badgeMeta?.name
                } badge!
          `,
          redirectRoute: `${BASE_URL}/feedback/${
            isSpeechcraft ? "messages-feedback" : "member-feedback"
          }`,
        };

      case NOTIFICATION_TYPES.badgeEarned:
        return {
          notificationText: (badge?.contentGroup || '').toLowerCase() === EXPLORATIONS_CONTENT_GROUP_VALUE
            ? `Congratulations! You've earned a new exploration badge`
            : `Congratulations! You've earned a new ${
                  badgeType === BADGE_TYPES.speechBadge
                    ? 'speech'
                    : badgeType === BADGE_TYPES.learnerBadge
                    ? badge.level
                      ? 'level'
                      : 'path'
                    : ''
              } badge`,
          redirectRoute: `${BASE_URL}/achievements${
            BADGE_TYPES.learnerBadge || BADGE_TYPES.speechBadge
              ? BADGE_HASH_TYPES.learnerBadge
              : BADGE_HASH_TYPES.feedbackBadge
          }`,
        };

      case NOTIFICATION_TYPES.evaluationRequest:
        return {
          notificationText: `📋 ${getUserName(
            notification.sender,
            true
          )} has identified you as an evaluator for their ${dayjs(
            speechLog?.speechDate
          )?.format("MMMM DD")} speech! Click to access the evaluation form.`,
          redirectRoute: `${BASE_URL}/requests`,
        };

      case NOTIFICATION_TYPES.evaluationRequestReview:
        return {
          notificationText: `🔔 You have a new speech evaluation! See what ${getUserName(
            notification.sender,
            true
          )} shared.`,
          redirectRoute: `${BASE_URL}/feedback/evaluation-form/${target?.id}?type=submitted`,
        };

      case NOTIFICATION_TYPES.completionReviewNotifyBCM:
        return {
          notificationText: getCompletionReviewNotifyBCMText(
            isApprovedByAdmin,
            approvedBy,
            status,
            requestType,
            member,
            levelNo,
            pathName,
            modified,
            levelName,
            isExploration
          ),
          redirectRoute:
            member.id === user.userId
              ? `/${isSpeechcraftLearning}/course/${courseId}/home`
              : `${BASE_URL}/bcm-dashboard/approvals/${slug}`,
          isSenderReceiver: member.id === user.userId,
        };

      case NOTIFICATION_TYPES.resourceShared:
        return {
          notificationText: `${getUserName(
            notification.sender,
            true
          )} shared the ${fileName} with you!`,
          redirectRoute: `${BASE_URL}/notifications/resources/${slug}`,
        };

      case NOTIFICATION_TYPES.evaluationDirectReview:
        return {
          notificationText: "I have filled out your Evaluation form",
          redirectRoute: `${BASE_URL}/feedback/evaluation-form/${target?.id}?type=submitted`,
        };

      case NOTIFICATION_TYPES.preCourseEnrollment:
        return {
          notificationText: `Thank you for buying ${courseName} again. You will be notified once re-enrollment is complete`,
          redirectRoute: "",
        };

      case NOTIFICATION_TYPES.completionRequestFirstReminder:
      case NOTIFICATION_TYPES.completionRequestSecondReminder:
        return {
          notificationText: getCompletionRequestReminderText(
            member,
            requestType,
            levelNo,
            pathName,
            type,
            levelName,
            isExploration
          ),
          redirectRoute: checkCondition
            ? `${BASE_URL}/bcm-dashboard/approvals/${slug}`
            : "",
          isDeleted: !checkCondition,
        };

      case NOTIFICATION_TYPES.courseInactivity:
        return {
          notificationText: `💡 It’s been a while since you worked on ${enrollment?.courseName}. Take a look at where you left off.`,
          redirectRoute: `/${isSpeechcraftLearning}/course/${enrollment?.courseId}/home`,
        };

      case NOTIFICATION_TYPES.pathwaysMentorProgramEnrollment:
        return {
          notificationText: `✨ You’ve decided to elevate your leadership skills as a Pathways Mentor! Get started in the program today.`,
          redirectRoute: `/${isSpeechcraftLearning}/course/${courseId}/home`,
        };

      case NOTIFICATION_TYPES.clubCoachModuleEnrollment:
        return {
          notificationText:
            "You’re registered for the Club Coach Module! Get started to unlock more leadership potential and build your coaching skills!",
          redirectRoute: `/${isSpeechcraftLearning}/course/${courseId}/home`,
        };

      case NOTIFICATION_TYPES.speechcrafterCertificate:
        return {
          notificationText:
            "🙌 You completed Speechcraft! Access your Speechcraft certificate here.",
          redirectRoute: notification.data?.certificateUrl,
        };

      case NOTIFICATION_TYPES.speechcrafterLastSelfAssessment:
        return {
          notificationText:
            "💭 Reflect on your growth by reviewing your self-assessments",
          redirectRoute: `${BASE_URL}/self-assessments/assessment-results`,
        };

      case NOTIFICATION_TYPES.speechcraftEventEnd:
        return {
          notificationText: "❗ It's time to close your Speechcraft event!",
          redirectRoute: "",
          hasOpenAction: false,
        };

      case NOTIFICATION_TYPES.reverse_completion_project:
        return {
          notificationText: `Your ${data.pathName} ${data.projectName} project completion has been reversed. Have questions or need assistance? Email&nbsp;<a href="mailto:educationprogram@toastmasters.org">educationprogram@toastmasters.org</a>.`,
          redirectRoute: "",
          hasOpenAction: false,
        };

      case NOTIFICATION_TYPES.reverse_completion_level: {
        const sectionLabel = (data.contentGroup || '').toLowerCase() === EXPLORATIONS_CONTENT_GROUP_VALUE
          ? data.sectionName
          : `Level ${data.levelNumber}`;

        return {
          notificationText: `Your ${data.pathName} ${sectionLabel} completion has been reversed. Have questions or need assistance? Email&nbsp;<a href="mailto:educationprogram@toastmasters.org">educationprogram@toastmasters.org</a>.`,
          redirectRoute: '',
          hasOpenAction: false,
        };
      }

      case NOTIFICATION_TYPES.reverse_completion_path: {
        const pathLabel = (data.contentGroup || '').toLowerCase() === EXPLORATIONS_CONTENT_GROUP_VALUE 
          ? 'exploration' : 'path';

        return {
          notificationText: `Your ${data.pathName} ${pathLabel} completion has been reversed. Have questions or need assistance? Email&nbsp;<a href="mailto:educationprogram@toastmasters.org">educationprogram@toastmasters.org</a>.`,
          redirectRoute: '',
          hasOpenAction: false,
        };
      }

      default:
        return { notificationText: "", redirectRoute: "" };
    }
  };

  const {
    notificationText,
    redirectRoute,
    isSenderReceiver = false,
    isDeleted = false,
    hasOpenAction = true,
  } = getNotificationText();

  const shouldRedirectToToastmasterWebsite =
    requestType?.toLocaleLowerCase() === "path" &&
    status === APPROVAL_REQUEST_STATUS.Approved &&
    type === NOTIFICATION_TYPES.completionReview;

  const displayName = getNotificationSenderName(
    notification.sender,
    type,
    requestType,
    isApprovedByAdmin
  );

  return {
    status,
    notificationText,
    redirectRoute,
    isSenderReceiver,
    shouldRedirectToToastmasterWebsite,
    notificationType: type,
    displayName,
    isDeleted,
    hasOpenAction,
  };
};
